import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockMemorials } from '../data/mockData';
import CreateMemorialModal from '../components/Modals/CreateMemorialModal';
import ObituaryHelperModal from '../components/Modals/ObituaryHelperModal';
import { writeFetch } from '../api';

const {
  FiBookOpen, FiUsers, FiCalendar, FiArrowRight,
  FiChevronLeft, FiChevronRight, FiShield, FiMusic,
} = FiIcons;

const normaliseDbMemorial = (page, loMap) => {
  const lo = loMap[page.loved_one_id] || {};
  const fullName = [lo.first_name, lo.last_name].filter(Boolean).join(' ') || page.title || 'Unknown';
  return {
    id: `db-${page.id}`,
    name: fullName,
    birthDate: lo.date_of_birth || '1900-01-01',
    deathDate: lo.date_of_passing || new Date().toISOString().slice(0, 10),
    tagline: page.description || lo.biography || 'A life beautifully lived.',
    coverImage: lo.cover_image || 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=2070&auto=format&fit=crop',
    profileImage: lo.profile_image || 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1288&auto=format&fit=crop',
  };
};

const Home = () => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [dbMemorials, setDbMemorials] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const scrollRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const fetchRecent = async () => {
      setLoadingRecent(true);
      try {
        const pagesRes = await writeFetch('read/memorial_pages?is_published=1&limit=20&sort=created_at&order=desc');
        const pagesData = await pagesRes.json();
        const pages = pagesData?.data || [];
        if (pages.length === 0) {
          setDbMemorials([]);
          return;
        }

        const loRes = await writeFetch('read/loved_ones?limit=100');
        const loData = await loRes.json();
        const loMap = {};
        (loData?.data || []).forEach(lo => { loMap[lo.id] = lo; });

        setDbMemorials(pages.map(p => normaliseDbMemorial(p, loMap)));
      } catch (e) {
        console.error('Home fetch error:', e);
      } finally {
        setLoadingRecent(false);
      }
    };

    fetchRecent();
    const handler = () => fetchRecent();
    window.addEventListener('memorial-created', handler);
    return () => window.removeEventListener('memorial-created', handler);
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setScrollProgress((scrollLeft / (scrollWidth - clientWidth)) * 100);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', handleScroll);
      return () => el.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scroll = (dir) => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: dir === 'left' ? -clientWidth : clientWidth, behavior: 'smooth' });
    }
  };

  const dbNames = new Set(dbMemorials.map(m => m.name.toLowerCase()));
  const combinedMemorials = [
    ...dbMemorials,
    ...mockMemorials.filter(m => !dbNames.has(m.name.toLowerCase())),
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=2070&auto=format&fit=crop"
            alt="Peaceful landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-accent-navy/40 to-brand-light/95" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 relative z-10 text-center text-white">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1 }} className="mb-6 inline-block">
            <span className="bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full text-xs font-black tracking-[0.3em] uppercase">
              Digital Sanctuary
            </span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-4xl sm:text-6xl md:text-7xl lg:text-9xl font-serif mb-8 leading-[1.05] tracking-tight">
            Preserving Legacies,<br />Honoring Lives
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
            className="text-lg md:text-2xl font-light mb-12 max-w-3xl mx-auto text-white/90 leading-relaxed px-4">
            Create a beautiful space where memories live on, communities gather, and the essence of your loved one is preserved forever with dignity.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: 'easeOut', delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <button onClick={() => setIsCreateOpen(true)}
              className="bg-accent-gold text-white px-10 py-5 rounded-full text-xl font-black uppercase tracking-widest hover:bg-white hover:text-accent-navy transition-all shadow-2xl w-full sm:w-auto transform hover:-translate-y-1">
              Create Memorial
            </button>
            <Link to="/explore"
              className="bg-white/10 backdrop-blur-md border-2 border-white text-white px-10 py-5 rounded-full text-xl font-black uppercase tracking-widest hover:bg-white hover:text-accent-navy transition-all w-full sm:w-auto">
              Explore Tributes
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 md:py-32 bg-brand-light">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-serif mb-6 text-accent-navy">A Complete Ecosystem of Care</h2>
            <p className="text-text-muted text-lg md:text-xl max-w-2xl mx-auto font-light">Everything you need to honor, manage, and reflect during times of transition.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            <FeatureCard icon={FiBookOpen} title="Digital Memorials" desc="Beautifully crafted pages with timelines, stories, and photo galleries that capture the true essence of a life well-lived." />
            <FeatureCard icon={FiUsers} title="Community Tributes" desc="A safe, respectful space for friends and family to leave memories, light virtual candles, and support one another globally." />
            <FeatureCard icon={FiCalendar} title="Event Management" desc="Seamlessly organize wakes, funerals, and memorial services with smart RSVP tracking and integrated high-quality livestreams." />
            <FeatureCard icon={FiShield} title="Digital Estate Suite" desc="Secure your legacy with professional tools for online will creation, asset organization, and legal document storage." />
            <FeatureCard icon={FiMusic} title="Service Programme" desc="Plan funeral and memorial service hymn lyrics, readings, and music with rights-compliant content and shareable booklets." color="amber" />
          </div>
        </div>
      </section>

      {/* Recent Tributes Slider */}
      <section className="py-24 md:py-32 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-16 gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-4xl md:text-6xl font-serif mb-4 text-accent-navy">Recent Tributes</h2>
              <p className="text-text-muted text-lg">Honoring beautiful lives across the globe.</p>
            </div>
            <Link to="/explore"
              className="text-accent-gold font-black uppercase tracking-widest text-sm hover:text-accent-navy transition-colors flex items-center gap-2 group">
              View all memorials <SafeIcon icon={FiArrowRight} className="transition-transform group-hover:translate-x-2" />
            </Link>
          </div>

          {loadingRecent ? (
            <div role="status" aria-label="Loading recent tributes" className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[0, 1, 2].map(index => (
                <div key={index} className="h-96 rounded-[40px] border border-brand-dark bg-brand-light animate-pulse" />
              ))}
            </div>
          ) : combinedMemorials.length === 0 ? (
            <div className="text-center py-24 bg-brand-light rounded-[40px] border-2 border-dashed border-brand-dark">
              <SafeIcon icon={FiBookOpen} className="w-12 h-12 text-text-muted/30 mx-auto mb-4" />
              <h3 className="text-2xl font-serif text-accent-navy mb-2">No tributes to show yet</h3>
              <p className="text-text-muted font-light">Published memorials will appear here.</p>
            </div>
          ) : (
            <div className="relative group/slider">
              <button onClick={() => scroll('left')}
                className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-2xl hidden md:flex items-center justify-center text-accent-navy hover:text-accent-gold transition-all opacity-0 group-hover/slider:opacity-100 border border-brand-dark">
                <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
              </button>
              <div ref={scrollRef}
                className="flex overflow-x-auto gap-8 pb-12 snap-x snap-mandatory hide-scrollbar scroll-smooth">
                {combinedMemorials.map((memorial) => (
                  <div key={memorial.id} className="w-[85vw] sm:w-[50vw] md:w-[calc(33.333%-1.35rem)] flex-shrink-0 snap-center md:snap-start">
                    <MemorialCard memorial={memorial} />
                  </div>
                ))}
              </div>
              <button onClick={() => scroll('right')}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-2xl hidden md:flex items-center justify-center text-accent-navy hover:text-accent-gold transition-all opacity-0 group-hover/slider:opacity-100 border border-brand-dark">
                <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 flex justify-center mt-12">
                <div className="w-48 h-1 bg-brand-light rounded-full overflow-hidden relative">
                  <motion.div className="absolute top-0 left-0 h-full bg-accent-gold"
                    style={{ width: `${Math.max(10, scrollProgress)}%` }}
                    transition={{ type: 'spring', stiffness: 100, damping: 20 }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <CreateMemorialModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
      <ObituaryHelperModal isOpen={isHelperOpen} onClose={() => setIsHelperOpen(false)} />
    </div>
  );
};

const FeatureCard = ({ icon, title, desc, color }) => (
  <motion.div whileHover={{ y: -10 }}
    className="bg-white p-8 md:p-10 rounded-[40px] shadow-sm border border-brand-dark hover:shadow-2xl transition-all duration-500 flex flex-col h-full group">
    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 shadow-inner transition-colors duration-500 ${
      color === 'amber' ? 'bg-amber-50 text-amber-600 group-hover:bg-amber-600 group-hover:text-white' : 'bg-brand-light text-accent-gold group-hover:bg-accent-navy group-hover:text-white'
    }`}>
      <SafeIcon icon={icon} className="w-8 h-8" />
    </div>
    <h3 className="text-2xl font-serif mb-4 text-accent-navy">{title}</h3>
    <p className="text-text-muted leading-relaxed font-light">{desc}</p>
  </motion.div>
);

const MemorialCard = ({ memorial }) => (
  <Link to={`/memorial/${memorial.id}`} className="group block h-full">
    <div className="bg-white rounded-[40px] overflow-hidden shadow-sm border border-brand-dark h-full flex flex-col transition-all duration-500 group-hover:shadow-2xl group-hover:border-accent-gold/20">
      <div className="h-64 md:h-80 relative overflow-hidden">
        <img src={memorial.coverImage} alt={memorial.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-accent-navy/90 via-transparent to-transparent opacity-60" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <p className="text-xs font-black uppercase tracking-[0.2em] mb-2 text-accent-gold">
            {new Date(memorial.birthDate).getFullYear()} — {new Date(memorial.deathDate).getFullYear()}
          </p>
          <h3 className="font-serif text-3xl md:text-4xl line-clamp-1">{memorial.name}</h3>
        </div>
      </div>
      <div className="p-8 md:p-10 flex-grow flex flex-col">
        <p className="text-lg text-text-muted line-clamp-2 mb-8 italic font-light leading-relaxed">"{memorial.tagline}"</p>
        <div className="mt-auto flex items-center gap-3 text-sm font-black uppercase tracking-widest text-accent-navy group-hover:text-accent-gold transition-colors">
          <span>Explore Legacy</span>
          <SafeIcon icon={FiArrowRight} className="w-5 h-5 transition-transform group-hover:translate-x-2" />
        </div>
      </div>
    </div>
  </Link>
);

export default Home;